/* eslint-disable no-undef */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import useApp from "../../hooks/useApp";
import useNavigateWithQuery from "../../hooks/useNavigateWithQuery";
import { logAnalyticEvent } from "../../analytics";
import {
  BODY_TYPES,
  EVENT_NAME,
  FOOTER_TYPES,
  HEADER,
  HEADER_TYPES,
  HOW_IT_WORKS_CARDS_LINK_ACTION,
  ROUTES,
  STORAGE_KEYS,
  FLEET_FINANCE_REQUIRED_DOCUMENTS,
} from "../../constants";
import { convertTimestamp, getApiErrorMessage } from "../../utils";
import { getSessionStorage, setSessionStorage } from "../../utils/storageUtils";
import api from "../../services";
import { templateSchema } from "../../utils/schemaUtils";
import {
  AnimatedSlider,
  CustomErrorModal,
  CustomLoader,
  TermsAndPrivacyModal,
  DocumentListModal,
} from "../../components";
import "./styles.scss";
import { ToastService } from "../../services/toaster.service";

const Onboarding = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigateWithQuery();
  const { pathname } = location;
  const routeName = pathname.split("/")[1];

  const {
    handleOnboardingCompleted,
    changeLanguage,
    selectedLanguage,
    tenantConfig,
    preLoginScreens,
    showOnboarding,
    handlePreLoginCompleted,
    handleBackToOnboarding,
    product,
    channel,
    getTenantConfig,
    consent,
    handleConsentCheck,
    isRegexValid,
  } = useApp();
  const OTP_LEN = tenantConfig?.otp_setting?.content?.length ?? 0;
  const productId = getSessionStorage(STORAGE_KEYS.PRODUCT_ID);
  const sessionChannel = getSessionStorage(HEADER.CHANNEL_ID);
  const [activeIndex, setActiveIndex] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [campaignSchema, setCampaignSchema] = useState(null);
  const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [direction, setDirection] = useState(0);
  const [showTermsAndPrivacyModal, setShowTermsAndPrivacyModal] =
    useState(false);
  const [termsAndPrivacyData, setTermsAndPrivacyData] = useState(null);
  const [formState, setFormState] = useState({});
  const [screenHistory, setScreenHistory] = useState([]);
  const [crResponse, setCrResponse] = useState(null);
  const [verifyOtpResponse, setVerifyOtpResponse] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [error, setError] = useState(null);
  const [showDocumentListModal, setShowDocumentListModal] = useState(false);
  const [modalDocType, setModalDocType] = useState(null);
  const [otpAttemptLeft, setOtpAttemptLeft] = useState(null);
  const [lockoutDuration, setLockoutDuration] = useState(null);
  const [startScreenTime, setStartScreenTime] = useState(0);

  const resetTimeoutRef = useRef(null);

  const getLangDirection = () =>
    selectedLanguage?.direction === "rtl" ? -1 : 1;

  const shouldShowCalculator =
    (tenantConfig?.slug === "snb" || tenantConfig?.slug === "alinma") &&
    channel === "GEIDEA";

  const trackScreenVisit = (screenId, status) => {
    const screenEndTime = Date.now();
    if (screenId) {
      logAnalyticEvent(EVENT_NAME.PAGE_VIEW, {
        page_title: screenId,
        path: `${window.location.origin}/${screenId}`,
        end_time: screenEndTime,
        start_time: startScreenTime,
        time_spent: convertTimestamp(screenEndTime - startScreenTime),
      });
    }
  };

  const handleOtpError = (error) => {
    const attemptsLeft = error?.response?.data?.errors?.[0]?.meta?.attemptLeft;

    if (attemptsLeft !== undefined) {
      setOtpAttemptLeft(attemptsLeft);

      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }

      resetTimeoutRef.current = setTimeout(() => {
        setOtpAttemptLeft(null);
      }, 36000);
    }
  };

  const handleOnClickSkip = () => {
    const endTime = Date.now();
    const timeSpent = convertTimestamp(endTime - startTime);

    logAnalyticEvent(EVENT_NAME.SKIP_ONBOARDING, {
      time_spent: timeSpent,
      screens_viewed: screenHistory.length,
      total_screens: preLoginScreens?.length || 0,
    });

    setDirection(getLangDirection());
    setActiveIndex(0);
    setCampaignSchema(null);

    if (currentScreenIndex < preLoginScreens.length - 1) {
      const nextIndex = currentScreenIndex + 1;
      setCurrentScreenIndex(nextIndex);
      setScreenHistory((prev) => [...prev, currentScreenIndex]);
    } else {
      handleOnboardingCompleted(timeSpent);
      completeOnboardingFlow();
    }
  };

  const completeOnboardingFlow = () => {
    const endTime = Date.now();
    const timeSpent = convertTimestamp(endTime - startTime);

    if (shouldShowCalculator) {
      navigate(`/${ROUTES.CHECK_ELIGIBILITY}`);
    } else {
      handlePreLoginCompleted();
      navigate("/");
    }
    // Track completion
    logAnalyticEvent(EVENT_NAME.ONBOARDING_COMPLETED, {
      time_spent: timeSpent,
      total_screens_viewed: screenHistory.length + 1,
    });
  };

  const handleCRVerify = async ({ buttonText = [" "], resendOtp = false }) => {
    try {
      setIsLoading(true);
      const payload = {
        cr_number: formState?.cr_number,
        product_id: productId,
      };
      const CRRegisterInitiateRes = await api.crLogin(payload);
      setCrResponse(CRRegisterInitiateRes?.data);
      if (!resendOtp) {
        handleOnPress({ campaignLength: buttonText.length });
        logAnalyticEvent(EVENT_NAME.LOGIN, {});
      }
    } catch (error) {
      if (error?.response?.data?.name === "OTP_MAX_ATTEMP_EXCEPTION") {
        setError({
          message: t(`app.locked_out_heading`),
          description: "locked_out_description",
          hideButton: true,
        });
      } else if (error?.response?.data?.name === "APPLICATION_NOT_AVALIABLE") {
        setError({
          message: getApiErrorMessage(error),
          description: "cr_rejection_error_description",
          primaryButtonText: "enter_another_cr",
        });
      } else {
        setError({
          message: getApiErrorMessage(error),
          description: "",
          primaryButtonText: "enter_another_cr",
        });
      }
      logAnalyticEvent(EVENT_NAME.ERROR_EVENT, {
        error: `${error?.response?.data?.errors?.[0]?.message}`,
      });
      console.error("Error in checkIsCutomerExists method: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (buttonText = [" "]) => {
    try {
      setIsLoading(true);
      const payload = {
        cr_number: formState?.cr_number,
        request_id: crResponse?.request_id,
        code: formState?.code,
      };
      const verifyOtpRes = await api.verifyOtp(payload);
      logAnalyticEvent(EVENT_NAME.VERIFY_OTP, {});
      setVerifyOtpResponse(verifyOtpRes?.data);
      handleOnPress({ campaignLength: buttonText.length });
    } catch (error) {
      if (error?.response?.data?.name === "OTP_MAX_ATTEMP_EXCEPTION") {
        const retryAfterSeconds =
          error?.response?.data?.errors?.[0]?.meta?.retryAfterSeconds;
        setError({
          message: t(`app.locked_out_heading`),
          description: "locked_out_description",
          hideButton: true,
        });
        setLockoutDuration(retryAfterSeconds);
      } else {
        handleOtpError(error);
      }
      logAnalyticEvent(EVENT_NAME.ERROR_EVENT, {
        error: `${error?.response?.data?.errors?.[0]?.message}`,
      });
      setFormState({ ...formState, code: null });
      console.error("Error in handleVerifyOtp method: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDistributorLogin = async ({
    buttonText = [" "],
    resendOtp = false,
  }) => {
    try {
      setIsLoading(true);
      const payload = {
        contact_no: `+${selectedCountry?.country_code}${formState?.phone_no}`,
        product_id: productId,
      };
      const CRRegisterInitiateRes = await api.distributorLogin(payload);
      setCrResponse(CRRegisterInitiateRes?.data);
      if (!resendOtp) {
        handleOnPress({ campaignLength: buttonText.length });
        logAnalyticEvent(EVENT_NAME.LOGIN, {});
      }
    } catch (error) {
      if (error?.response?.data?.name === "OTP_MAX_ATTEMP_EXCEPTION") {
        setError({
          message: t(`app.locked_out_heading`),
          description: "locked_out_description",
          hideButton: true,
        });
      } else if (error?.response?.data?.name === "APPLICATION_NOT_AVALIABLE") {
        setError({
          message: getApiErrorMessage(error),
          description: "cr_rejection_error_description",
          primaryButtonText: "enter_another_cr",
        });
      } else {
        setError({
          message: getApiErrorMessage(error),
          description: "",
          primaryButtonText: "enter_another_cr",
        });
      }
      logAnalyticEvent(EVENT_NAME.ERROR_EVENT, {
        error: `${error?.response?.data?.errors?.[0]?.message}`,
      });
      console.error("Error in checkIsCutomerExists method: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDistributorVerifyOtp = async (buttonText = [" "]) => {
    try {
      setIsLoading(true);
      const payload = {
        product_id: productId,
        customer_id: crResponse?.customer_id,
        request_id: crResponse?.request_id,
        code: formState?.code,
        full_name: formState?.full_name,
        email: formState?.email,
        meta: {
          annual_revenue: formState?.annual_revenue,
          required_amount: formState?.amount_required,
        },
      };
      const verifyOtpRes = await api.distributorVerifyOtp(payload);
      logAnalyticEvent(EVENT_NAME.VERIFY_OTP, {});
      setVerifyOtpResponse(verifyOtpRes?.data);
      setSessionStorage(STORAGE_KEYS.LOCAL_FORM_STATE, formState);
      handleOnPress({ campaignLength: buttonText.length });
    } catch (error) {
      if (error?.response?.data?.name === "OTP_MAX_ATTEMP_EXCEPTION") {
        const retryAfterSeconds =
          error?.response?.data?.errors?.[0]?.meta?.retryAfterSeconds;
        setError({
          message: t(`app.locked_out_heading`),
          description: "locked_out_description",
          hideButton: true,
        });
        setLockoutDuration(retryAfterSeconds);
      } else {
        handleOtpError(error);
      }

      setFormState({ ...formState, code: null });
      logAnalyticEvent(EVENT_NAME.ERROR_EVENT, {
        error: `${error?.response?.data?.errors?.[0]?.message}`,
      });
      console.error("Error in handleVerifyOtp method: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnPress = ({ campaignLength, action }) => {
    if (activeIndex === campaignLength - 1) {
      const endTime = Date.now();
      const timeSpent = convertTimestamp(endTime - startTime);
      trackScreenVisit(preLoginScreens[currentScreenIndex], "completed");
      if (currentScreenIndex < preLoginScreens.length - 1) {
        const nextIndex = currentScreenIndex + 1;
        setCurrentScreenIndex(nextIndex);
        setActiveIndex(0);
        setCampaignSchema(null);
        setDirection(getLangDirection());
        setScreenHistory((prev) => [...prev, currentScreenIndex]);
      } else {
        completeOnboardingFlow();
      }
    } else {
      const nextIndex = (activeIndex + 1) % campaignLength;
      setDirection(getLangDirection());
      setActiveIndex(nextIndex);
    }
  };

  const handleGetCampaign = async (screenIndex) => {
    if (
      !preLoginScreens ||
      screenIndex < 0 ||
      screenIndex >= preLoginScreens.length
    ) {
      console.error("Invalid screen index or preLoginScreens not available");
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.getCampaignScreen(
        preLoginScreens[screenIndex]
      );
      setCampaignSchema(response?.data);

      // Track screen view
      setStartScreenTime(Date.now());
    } catch (error) {
      console.error("Error fetching screen", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackOnboarding = () => {
    if (activeIndex > 0) {
      setActiveIndex((prev) => prev - 1);
      setDirection(-getLangDirection());
    } else if (screenHistory.length > 0) {
      const previousScreenIndex = screenHistory[screenHistory.length - 1];
      const newHistory = screenHistory.slice(0, -1);

      setCurrentScreenIndex(previousScreenIndex);
      setScreenHistory(newHistory);
      setActiveIndex(0);
      setDirection(-getLangDirection());
      setCampaignSchema(null);
      handleBackToOnboarding();
    } else {
      handleBackToOnboarding();
    }
  };

  const handleDefaultBtnDisabled = (action) => {
    const validators = {
      verify_cr: () => !!formState?.cr_number && isRegexValid.length === 0,
      verify_otp: () => (formState?.code?.length ?? 0) === OTP_LEN,
      distributor_login: () => {
        const requiredFilled =
          !!formState?.phone_no &&
          !!formState?.amount_required &&
          !!formState?.annual_revenue &&
          !!formState?.email &&
          !!formState?.full_name;
        return requiredFilled && isRegexValid.length === 0;
      },

      verify_distributor_otp: () => (formState?.code?.length ?? 0) === OTP_LEN,
    };

    const canEnable = validators[action]?.() ?? true;

    return !canEnable;
  };

  useEffect(() => {
    return () => {
      const endTime = Date.now();
      logAnalyticEvent(EVENT_NAME.JOURNEY_START, {
        end_time: endTime,
        start_time: startTime,
        time_spent: convertTimestamp(endTime - startTime),
      });
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (
      tenantConfig?.user_consents?.content &&
      (!consent || Object.keys(consent).length === 0)
    ) {
      const initialConsent = {};
      tenantConfig.user_consents.content.forEach((item) => {
        initialConsent[item.position] = item.default_checked || false;
      });
      handleConsentCheck(initialConsent);
    }
    if (tenantConfig?.phone_number_policy?.content.allowed_countries.length) {
      const allowedCountries =
        tenantConfig?.phone_number_policy?.content.allowed_countries;
      setSelectedCountry(allowedCountries[0]);
    }
  }, [tenantConfig]);

  useEffect(() => {
    setStartTime(Date.now());
    if (preLoginScreens?.length > 0) {
      setScreenHistory([0]);
    }
  }, []);

  useEffect(() => {
    if (
      preLoginScreens?.length > 0 &&
      currentScreenIndex < preLoginScreens.length
    ) {
      handleGetCampaign(currentScreenIndex);
    }
  }, [currentScreenIndex, preLoginScreens]);

  useEffect(() => {
    getTenantConfig();
  }, [sessionChannel]);

  const isLastScreen = currentScreenIndex === preLoginScreens.length - 1;

  const areAllRequiredConsentsChecked = () => {
    if (!tenantConfig?.user_consents?.content || !consent) return true;
    const requiredConsents = tenantConfig.user_consents.content.filter(
      (consentItem) => consentItem.is_required
    );
    return requiredConsents.every(
      (consentItem) => consent[consentItem.position] === true
    );
  };

  const isConsentRequiredScreen = () => {
    const checkIsButtonDisabled = ["fleet-finance-phone-number-v1"];
    return checkIsButtonDisabled.includes(campaignSchema.slug);
  };

  const getCustomProps = (props) => {
    switch (props.type) {
      // Header Types
      case HEADER_TYPES.ONBOARDING_HEADER:
        return {
          onClick: () => {
            changeLanguage(selectedLanguage?.value === "ar" ? "en" : "ar");
            logAnalyticEvent(EVENT_NAME.LANGUAGE_CHANGE, {
              original_language:
                selectedLanguage?.value === "ar" ? "العربية" : "English",
              preffered_language:
                selectedLanguage?.value === "ar" ? "English" : "العربية",
            });
          },
        };
      case HEADER_TYPES.DEFAULT_HEADER:
        return {
          onClick: handleBackOnboarding,
          containerStyle: "header-container",
        };

      // Body Types
      case BODY_TYPES.HEADING: {
        const parseToArray = (value) => {
          if (!value || typeof value !== "string") return [];
          return value
            .replace(/[\[\]]/g, "")
            .split(",")
            .map((item) => item.trim())
            .filter((item) => item.length > 0);
        };

        const headingArray = parseToArray(props?.heading);
        const descriptionArray = parseToArray(props?.description);
        const screenActiveIndex =
          activeIndex >= 0 && activeIndex < headingArray.length
            ? activeIndex
            : 0;
        const selectedHeading = headingArray[screenActiveIndex] || "";
        const selectedDescription = descriptionArray[screenActiveIndex] || "";

        return {
          phoneNo: crResponse?.contact_no,
          heading: routeName
            ? selectedHeading
            : selectedHeading
              ? `app.${selectedHeading}`
              : "",
          description: routeName
            ? selectedDescription
            : selectedDescription
              ? `app.${selectedDescription}`
              : "",
          specialText: props?.specialText
            ? routeName
              ? props?.specialText
              : `app.${props?.specialText}`
            : "",
        };
      }
      case BODY_TYPES.IMAGE: {
        const parseToArray = (value) => {
          if (!value || typeof value !== "string") return [];
          return value
            .replace(/[\[\]]/g, "")
            .split(",")
            .map((item) => item.trim())
            .filter((item) => item.length > 0);
        };

        const imageArray = parseToArray(props?.image);
        return {
          image: imageArray,
          activeIndex: activeIndex,
          imageContainerClass: "custom-image-container",
        };
      }
      case BODY_TYPES.PARTNER_LIST_CARD: {
        const data = JSON.parse(props?.cardData);
        return {
          data,
          onClick: (bank) => {
            setSessionStorage(STORAGE_KEYS.PARTNER_TENANT_ID, bank.id);
            setSessionStorage(STORAGE_KEYS.PARTNER_TENANT_SLUG, bank.slug);
            setSessionStorage(STORAGE_KEYS.PARTNER_TENANT_NAME, bank.name);
            navigate("/");
            handlePreLoginCompleted();
          },
        };
      }
      case BODY_TYPES.OTP_INPUT:
        return {
          setCode: (code) => setFormState((prev) => ({ ...prev, code })),
          code: formState?.code || "",
          otpNotRecieve: t(
            `${routeName ? routeName : "app"}.${props?.otpNotRecieve}`
          ),
          resendCode: t(
            `${routeName ? routeName : "app"}.${props?.resendCode}`
          ),
          resend: t(`${routeName ? routeName : "app"}.${props?.resend}`),
          otpLength: props?.otpLength,
          otpAttemptLeft: otpAttemptLeft,
          resendOtp: () =>
            formState?.cr_number
              ? handleCRVerify({ resendOtp: true })
              : handleDistributorLogin({ resendOtp: true }),
        };
      case BODY_TYPES.FORM:
        return {
          setFormState: setFormState,
          formState: formState,
        };
      case BODY_TYPES.HOW_IT_WORKS_CARDS: {
        return {
          onClickLink: (action) => {
            if (action === HOW_IT_WORKS_CARDS_LINK_ACTION.SHOW_DOCUMENT_LIST) {
              setShowDocumentListModal(true);
              return;
            }
          },
        };
      }

      // Footer Types
      case FOOTER_TYPES.PAGINATION_DOT: {
        const paginationLength = Array.from(
          { length: parseInt(props?.numberOfDots) || 1 },
          (_, i) => i
        );
        return {
          pagination: paginationLength,
          activeIndex: activeIndex,
          currentIndex: activeIndex,
          containerClass: "pagination-wrapper",
        };
      }
      case FOOTER_TYPES.DEFAULT_BUTTON: {
        const buttonText = props?.buttonText
          .replace(/[\[\]\s]/g, "")
          .split(",");

        const buttonTextIndex = Math.min(activeIndex, buttonText.length - 1);
        const shouldDisableButton = isConsentRequiredScreen()
          ? !areAllRequiredConsentsChecked()
          : false;

        return {
          onClick: async () => {
            if (!shouldDisableButton) {
              const customActions = [
                "verify_cr",
                "verify_otp",
                "distributor_login",
                "verify_distributor_otp",
              ];
              if (customActions.includes(props?.action)) {
                props?.action === "verify_cr" &&
                  (await handleCRVerify({ buttonText }));
                props?.action === "verify_otp" &&
                  (await handleVerifyOtp(buttonText));
                props?.action === "distributor_login" &&
                  (await handleDistributorLogin(buttonText));
                props?.action === "verify_distributor_otp" &&
                  (await handleDistributorVerifyOtp(buttonText));
              } else {
                handleOnPress({ campaignLength: buttonText.length });
              }
            }
          },
          buttonText: routeName
            ? buttonText[buttonTextIndex]
            : `app.${buttonText[buttonTextIndex]}`,
          containerClassName: `${
            props?.buttonWrapperClass
              ? props?.buttonWrapperClass
              : "custom-button-wrapper "
          }`,
          disabled:
            handleDefaultBtnDisabled(props?.action) || shouldDisableButton,
        };
      }
      case FOOTER_TYPES.SKIP_BUTTON: {
        const buttonText = props?.buttonText
          .replace(/[\[\]\s]/g, "")
          .split(",");
        if (buttonText.length < activeIndex + 1) {
          return {
            buttonText: null,
          };
        } else {
          return {
            buttonText: routeName
              ? t(`${routeName}.${buttonText[activeIndex]}`)
              : t(`app.${buttonText[activeIndex]}`),
            onClick: () => handleOnClickSkip(),
          };
        }
      }
      case FOOTER_TYPES.ACCEPT_OUR_CONSENT: {
        return {
          value: consent || {},
          onChange: (newConsent) => {
            handleConsentCheck(newConsent);
          },
          onOpenTermsModal: (docType) => {
            setModalDocType(docType);
            if (docType === "privacy_policy") {
              setTermsAndPrivacyData(tenantConfig?.privacy_policy);
            } else {
              setTermsAndPrivacyData(tenantConfig?.terms_conditions);
            }
            setShowTermsAndPrivacyModal(true);
          },
        };
      }
      case FOOTER_TYPES.LINK_TEXT: {
        return {
          labelText: routeName ? props?.labelText : `app.${props?.labelText}`,
        };
      }
      default:
        return {};
    }
  };

  return (
    <>
      {campaignSchema?.content ? (
        <AnimatedSlider direction={direction} keyIndex={activeIndex}>
          <div id="onboarding-container">
            {templateSchema(campaignSchema?.content).map((element, index) => {
              const Component = element.Component;
              const props = element.props;
              const customProps = getCustomProps(props, index);
              return Component
                ? React.createElement(Component, {
                    key: `${currentScreenIndex}-${activeIndex}-${index}`,
                    ...element.props,
                    ...customProps,
                  })
                : null;
            })}
          </div>
        </AnimatedSlider>
      ) : null}
      <CustomLoader visible={isLoading} />
      <CustomErrorModal
        showPopup={!!error}
        title={error?.message}
        message={t(
          routeName
            ? `${routeName}.${error?.description}`
            : `app.${error?.description}`,
          {
            lockoutDuration: parseInt(lockoutDuration / 60),
          }
        )}
        primaryBtnText={t(
          routeName
            ? `${routeName}.${error?.primaryButtonText}`
            : `app.${error?.primaryButtonText}`
        )}
        secondaryBtnText={t(
          routeName ? `${routeName}.exit_application` : "app.exit_application"
        )}
        handlePrimaryButton={() => {
          setError(null);
          setFormState({});
          setCurrentScreenIndex(1);
          handleConsentCheck({}, { reset: true });
        }}
        handleSecondaryButton={() => {
          setError(null);
          setCurrentScreenIndex(0);
          handleConsentCheck({}, { reset: true });
        }}
        onClickClose={() => setError(null)}
        hideButton={error?.hideButton}
      />
      <TermsAndPrivacyModal
        visible={showTermsAndPrivacyModal}
        onClickClose={() => setShowTermsAndPrivacyModal(false)}
        data={termsAndPrivacyData}
        docType={modalDocType}
      />
      <DocumentListModal
        visible={showDocumentListModal}
        onClickClose={() => setShowDocumentListModal(false)}
        documents={FLEET_FINANCE_REQUIRED_DOCUMENTS}
        hideToolTip={true}
      />
    </>
  );
};

export default Onboarding;
